import { clsx } from "clsx";
import { format, isValid, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { type ComponentProps, useEffect, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

type CalendarType = ComponentProps<typeof Calendar>;

interface DatePickerProps extends ComponentProps<"input"> {
	selected: Date | undefined;
	onSelected: (next: Date | undefined) => void;
	modal?: boolean;
	calendarProps?: Omit<
		CalendarType,
		"mode" | "locale" | "showOutsideDays" | "ref"
	>;
}

export function DatePicker({
	calendarProps,
	className,
	disabled,
	id,
	modal,
	name,
	placeholder = "dd/mm/aaaa",
	selected,
	onSelected,
	...props
}: DatePickerProps) {
	const [month, setMonth] = useState(new Date());
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const externalText = selected ? format(selected, "dd/MM/yyyy") : "";
	const [text, setText] = useState(externalText);
	const lastSyncedRef = useRef(externalText);

	useEffect(() => {
		if (externalText !== lastSyncedRef.current) {
			lastSyncedRef.current = externalText;
			setText(externalText);
		}
	}, [externalText]);

	const handleDayPickerSelect = (date: Date | undefined) => {
		if (!date) {
			onSelected(undefined);
		} else {
			onSelected(date);
			setOpen(false);
		}
	};

	const handleChangeInput = (value: string) => {
		setText(value);

		if (!value) {
			lastSyncedRef.current = "";
			onSelected(undefined);
			return;
		}

		if (value.length < 10) return;

		const parsedDate = parse(value, "dd/MM/yyyy", new Date());
		if (!isValid(parsedDate)) return;

		lastSyncedRef.current = format(parsedDate, "dd/MM/yyyy");
		onSelected(parsedDate);
		setMonth(parsedDate);
	};

	return (
		<Popover open={open} onOpenChange={setOpen} modal={modal}>
			<div
				ref={wrapperRef}
				data-slot="date-picker"
				data-disabled={disabled ? "true" : undefined}
				className={clsx("date-picker", className)}
				aria-invalid={props["aria-invalid"]}
				onFocusCapture={(e) => {
					if (e.target instanceof HTMLInputElement) setOpen(true);
				}}
			>
				<IMaskInput
					className="date-picker__input"
					disabled={disabled}
					id={id}
					name={name}
					value={text}
					mask="00/00/0000"
					placeholder={placeholder}
					onAccept={(value: string) => handleChangeInput(value)}
				/>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="date-picker__trigger"
						disabled={disabled}
					>
						<CalendarDays className="date-picker__icon" />
					</button>
				</PopoverTrigger>
			</div>
			<PopoverContent
				align="end"
				className="date-picker__popover"
				onOpenAutoFocus={(e) => e.preventDefault()}
				onInteractOutside={(e) => {
					const target = e.target as Node | null;
					if (target && wrapperRef.current?.contains(target)) {
						e.preventDefault();
					}
				}}
			>
				<Calendar
					mode="single"
					month={month}
					onMonthChange={setMonth}
					selected={selected}
					onSelect={handleDayPickerSelect}
					locale={ptBR}
					showOutsideDays
					required
					{...calendarProps}
				/>
			</PopoverContent>
		</Popover>
	);
}
